import type { MouseEvent as ReactMouseEvent } from "react";
import "./PingVisionButton.scss";
import { Logo } from "@repo/ping-design";

import { VISION_URL } from "./Constants";

type PingVisionButtonProps = {
  disabled?: boolean;
};

const PingVisionButton = ({ disabled = false }: PingVisionButtonProps) => {
  const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <a
      className={`PingVisionButton${disabled ? " PingVisionButton--disabled" : ""}`}
      href={disabled ? undefined : VISION_URL}
      target={disabled ? undefined : "_blank"}
      rel={disabled ? undefined : "noreferrer"}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
      onClick={handleClick}
    >
      <div className="PingVisionButton__Content">
        <div className="PingVisionButton__Content__LogoContainer">
          <Logo className="PingVisionButton__Content__LogoContainer__Logo" />
          <p className="PingVisionButton__Content__LogoContainer__Vision">
            .Vision
          </p>
        </div>
        <div className="PingVisionButton__Content__Text">
          Control your Submission Workflow
        </div>
      </div>
      <div className="PingVisionButton__BgFilter" />
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 580 480"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="PingVisionButton__SVG"
      >
        <g opacity="0.4" filter="url(#filter0_f_340_13052)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M153.711 6.28391C216.316 6.63692 288.931 -70.6566 335.055 -28.3241C386.584 18.9685 315.42 112.614 340.642 177.849C360.005 227.929 439.773 234.751 456.497 285.773C473.488 337.611 451.915 395.095 427.906 444.079C402.974 494.944 367.698 543.22 317.469 569.411C267.944 595.234 209.546 585.622 153.711 587.051C96.4482 588.517 21.2159 622.333 -14.9595 577.919C-56.89 526.44 23.2464 437.637 -13.1948 382.136C-53.4427 320.838 -184.51 355.64 -206.78 285.773C-225.643 226.595 -118.948 200.069 -88.4805 145.944C-57.5171 90.9374 -83.4995 1.15645 -28.8174 -30.3759C25.8744 -61.9139 90.5787 5.92792 153.711 6.28391Z"
            fill="#00E6FF"
          />
        </g>
        <g opacity="0.5" filter="url(#filter1_f_340_13052)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M399.781 -158.167C450.011 -170.512 492.061 -117.357 537.665 -92.9494C584.843 -67.6988 647.583 -58.9614 673.057 -11.904C698.548 35.1829 683.259 94.8728 666.95 145.872C653.074 189.262 611.667 215.62 587.874 254.468C564.876 292.018 565.618 345.342 529.423 370.419C493.065 395.61 443.922 387.619 399.781 384.787C357.762 382.091 314.915 376.994 279.399 354.379C244.552 332.189 227.818 292.511 203.001 259.483C174.664 221.77 139.492 189.967 122.731 145.872C101.032 88.7894 49.9051 12.2859 92.342 -31.6277C140.375 -81.3327 232.245 -7.28816 296.164 -33.5967C346.975 -54.51 346.422 -145.053 399.781 -158.167Z"
            fill="#FDF16D"
          />
        </g>
        <g opacity="0.3" filter="url(#filter2_f_340_13052)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M470.08 163.313C524.635 176.855 545.227 245.523 594.1 273.293C643.483 301.353 725.968 274.787 753.601 324.411C781.397 374.327 717.987 431.128 713.715 488.102C709.887 539.163 744.571 589.1 730.072 638.209C714.463 691.08 685.191 755.788 631.282 767.312C572.526 779.872 529.873 687.888 470.08 693.786C399.173 700.782 360.24 808.731 289.405 801.04C227.467 794.315 187.816 720.993 170.214 661.23C153.425 604.23 191.864 547.279 197.233 488.102C202.011 435.444 181.525 381.726 200.064 332.208C219.97 279.043 256.435 231.528 304.565 201.422C353.396 170.879 414.18 149.437 470.08 163.313Z"
            fill="#FF00D0"
          />
        </g>
        <defs>
          <filter
            id="filter0_f_340_13052"
            x="-309"
            y="-141"
            width="871.574"
            height="841.747"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="50"
              result="effect1_foregroundBlur_340_13052"
            />
          </filter>
          <filter
            id="filter1_f_340_13052"
            x="-24"
            y="-260"
            width="810.634"
            height="748.255"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="50"
              result="effect1_foregroundBlur_340_13052"
            />
          </filter>
          <filter
            id="filter2_f_340_13052"
            x="66"
            y="59"
            width="794.623"
            height="842.432"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="50"
              result="effect1_foregroundBlur_340_13052"
            />
          </filter>
        </defs>
      </svg>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 580 480"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="PingVisionButton__SVG"
      >
        <g opacity="0.15" filter="url(#filter0_f_340_13056)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M203.08 102.313C257.635 115.855 278.227 184.523 327.1 212.293C376.483 240.353 458.968 213.787 486.601 263.411C514.397 313.327 450.987 370.128 446.715 427.102C442.887 478.163 477.571 528.1 463.072 577.209C447.463 630.08 418.191 694.788 364.282 706.312C305.526 718.872 262.873 626.888 203.08 632.786C132.173 639.782 93.2396 747.731 22.4048 740.04C-39.533 733.315 -79.1839 659.993 -96.7864 600.23C-113.575 543.23 -75.1357 486.279 -69.7667 427.102C-64.9893 374.444 -85.4746 320.726 -66.9356 271.208C-47.0304 218.043 -10.5646 170.528 37.5653 140.422C86.3958 109.879 147.18 88.4372 203.08 102.313Z"
            fill="#3EE554"
          />
        </g>
        <defs>
          <filter
            id="filter0_f_340_13056"
            x="-201"
            y="-2"
            width="794.623"
            height="842.432"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="50"
              result="effect1_foregroundBlur_340_13056"
            />
          </filter>
        </defs>
      </svg>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 580 421"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="PingVisionButton__SVG"
      >
        <g opacity="0.15" filter="url(#filter0_f_340_13057)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M184.711 -273.716C247.316 -273.363 319.931 -350.657 366.055 -308.324C417.584 -261.032 346.42 -167.386 371.642 -102.151C391.005 -52.0711 470.773 -45.2492 487.497 5.77304C504.488 57.6112 482.915 115.095 458.906 164.079C433.974 214.944 398.698 263.22 348.469 289.411C298.944 315.234 240.546 305.622 184.711 307.051C127.448 308.517 52.2159 342.333 16.0405 297.919C-25.89 246.44 54.2464 157.637 17.8052 102.136C-22.4427 40.8381 -153.51 75.6401 -175.78 5.77304C-194.643 -53.4046 -87.948 -79.9314 -57.4805 -134.056C-26.5171 -189.063 -52.4995 -278.844 2.18256 -310.376C56.8744 -341.914 121.579 -274.072 184.711 -273.716Z"
            fill="#737AFF"
          />
        </g>
        <defs>
          <filter
            id="filter0_f_340_13057"
            x="-278"
            y="-421"
            width="871.574"
            height="841.747"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="50"
              result="effect1_foregroundBlur_340_13057"
            />
          </filter>
        </defs>
      </svg>
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 514 480"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="PingVisionButton__SVG"
      >
        <g opacity="0.15" filter="url(#filter0_f_340_13058)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M423.781 27.8332C474.011 15.4884 516.061 68.6428 561.665 93.0506C608.843 118.301 671.583 127.039 697.057 174.096C722.548 221.183 707.259 280.873 690.95 331.872C677.074 375.262 635.667 401.62 611.874 440.468C588.876 478.018 589.618 531.342 553.423 556.419C517.065 581.61 467.922 573.619 423.781 570.787C381.762 568.091 338.915 562.994 303.399 540.379C268.552 518.189 251.818 478.511 227.001 445.483C198.664 407.77 163.492 375.967 146.731 331.872C125.032 274.789 73.9051 198.286 116.342 154.372C164.375 104.667 256.245 178.712 320.164 152.403C370.975 131.49 370.422 40.947 423.781 27.8332Z"
            fill="#FD6DBA"
          />
        </g>
        <defs>
          <filter
            id="filter0_f_340_13058"
            x="0"
            y="-74"
            width="810.634"
            height="748.255"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feGaussianBlur
              stdDeviation="50"
              result="effect1_foregroundBlur_340_13058"
            />
          </filter>
        </defs>
      </svg>
    </a>
  );
};

export default PingVisionButton;
